import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { fetchApi } from "@/lib/api/index.ts";
import { z } from "zod";
import { useToast } from "@/components/ui/use-toast.ts";
import { User as UserType } from "@/lib/api/user.ts";

export interface Reaction {
  emoji: string;
  count: number;
  users: Array<{
    id: string;
    name: string;
  }>;
}

export interface Message {
  type: "MESG" | "FILE" | "ADMM";
  message_id: number;
  message: string;
  data: string;
  custom_type: string;
  file: {
    name: string;
    url: string;
    type: string;
    data: string;
    size: number;
  };
  created_at: number;
  user: User;
  channel_url: string;
  req_id: string;
  updated_at: number;
  is_removed: boolean;
  is_op_msg: boolean;
  parent_message_id: number | null;
  parent_message_info?: Message;
  reactions: Reaction[];
  translations?: Record<string, string>;
  riskFlag?: {
    alertId: string;
    status: string;
    category: string;
    priority: string;
  } | null;
}

export interface User {
  user_id: string;
  profile_url: string;
  nickname: string;
  metadata: {
    role?: string;
    is_supervisor?: string;
  };
  role: string;
  is_active: boolean;
}

interface MessagesResponse {
  messages: Message[];
  hasMore?: boolean;
  nextCursor: number | undefined;
}

export const useMessages = (mentorshipId?: string) => {
  return useInfiniteQuery({
    queryKey: ["messages", mentorshipId],
    initialPageParam: 0,
    queryFn: async ({ pageParam = undefined }) => {
      const { data } = await fetchApi<MessagesResponse>({
        url: `v2/admin/mentorships/${mentorshipId}/messages`,
        method: "GET",
        params: { messageId: pageParam || undefined },
      });

      console.log("data", data);

      if (!data?.messages) {
        return { messages: [], nextCursor: undefined, hasMore: false };
      }

      const messages = data.messages;

      return {
        messages,
        nextCursor: data?.hasMore ? data?.nextCursor : undefined,
        hasMore: !!data?.hasMore,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!mentorshipId,
  });
};

export const useMessagesFromStart = (
  mentorshipId?: string,
  enabled = false,
) => {
  return useInfiniteQuery({
    queryKey: ["messages-from-start", mentorshipId],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await fetchApi<MessagesResponse>({
        url: `v2/admin/mentorships/${mentorshipId}/messages`,
        method: "GET",
        params: {
          fromStart: true,
          messageId: pageParam || undefined,
        },
      });

      if (!data?.messages) {
        return { messages: [], nextCursor: undefined, hasMore: false };
      }

      return {
        messages: data.messages,
        nextCursor: data?.hasMore ? data?.nextCursor : undefined,
        hasMore: !!data?.hasMore,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && !!mentorshipId,
  });
};

interface Settings {
  name: string;
  value: Record<string, unknown>;
}

export const useFetchSettings = (enabled?: boolean) => {
  return useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data } = await fetchApi<{ settings: Settings[] }>({
        url: "v2/admin/settings",
        method: "GET",
      });

      if (!data?.settings) {
        return [];
      }

      return data.settings;
    },
    enabled,
  });
};

export const promptSettingsSchema = z.object({
  chatSystemPrompt: z.string(),
  suggestedRepliesPrompt: z.string(),
  mentorSubheadingGeneratorPrompt: z.string(),
  onboardingThirdMessage: z.string(),
  suggestedOpenerPrompt: z.string(),
  messageCount: z.number(),
});

export type PromptSettingsSchema = z.infer<typeof promptSettingsSchema>;

export const useUpdatePromptSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["prompt-settings"],
    mutationFn: async (payload: PromptSettingsSchema) => {
      const { data } = await fetchApi({
        url: "v2/admin/settings/prompt",
        method: "PUT",
        data: payload,
      });

      if (!data) {
        throw new Error("Failed to update prompt settings");
      }

      return data;
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["settings"],
      });
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Settings have been updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update settings",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

export const reactivationSettingsSchema = z.object({
  reactivationMessage: z.string(),
});

export type ReactivationSettingsSchema = z.infer<
  typeof reactivationSettingsSchema
>;

export const useUpdateReactivationSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["reactivation-settings"],
    mutationFn: async (payload: ReactivationSettingsSchema) => {
      const { data } = await fetchApi({
        url: "v2/admin/settings/reactivation",
        method: "PUT",
        data: payload,
      });

      if (!data) {
        throw new Error("Failed to update reactivation settings");
      }

      return data;
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["settings"],
      });
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Reactivation settings have been updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update settings",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

interface MentorshipLite {
  id: string;
  deactivated: boolean;
  mentee: UserType;
  mentor: UserType;
}

export const useGetLiteMentorships = () => {
  return useQuery({
    queryKey: ["lite-mentorships"],
    queryFn: async () => {
      const { data } = await fetchApi<{ mentorships: MentorshipLite[] }>({
        url: "/v2/admin/mentorships/lite",
        method: "GET",
      });

      if (!data?.mentorships) {
        return [];
      }

      return data.mentorships;
    },
  });
};

export const useClearCache = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["clear-cache"],
    mutationFn: async () => {
      const { data } = await fetchApi({
        url: "v2/admin/clear-cache",
        method: "POST",
      });

      if (!data) {
        throw new Error("Failed to clear cache");
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Cache cleared",
        description: "The cache has been cleared successfully",
      });
      // Invalidate all queries to refetch fresh data
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast({
        title: "Failed to clear cache",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

interface TeenData {
  first_name: string;
  last_name: string;
  mentee_phone: string;
  address: string | null;
  date_of_birth: string | null;
  start_date: string | null;
  end_date: string | null;
  total_days: number;
  days_in_mentorship: number | null;
  mentee_id: string;
  mentee_name: string;
  current_mentor?: string;
  satisfaction_rating: number | null;
  teen_communication_days: number;
  mentor_communication_days: number;
  teen_messages_count: number;
  mentor_messages_count: number;
  video_call_count: number;
  total_call_duration: number;
  speed_to_care: number | null;
  phq8_score: number | null;
  gad7_score: number | null;
  cssrs_response: string | null;
  crisis_resource_utilization: number | null;
  app_open_count: number | null;
  engagement_status: "engaged" | "late start" | "not engaged";
  start_with_somethings: string | null;
  zipcode: string | null;
  insurance_provider?: string | null;
}

export const exportTeenData = async (
  startDate: Date,
  endDate: Date,
  groupIds: string[] | "ALL",
  userStatus: "active" | "inactive" | "all",
): Promise<TeenData[]> => {
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    userStatus,
    groupIds: groupIds === "ALL" ? "ALL" : JSON.stringify(groupIds),
  });

  const { data } = await fetchApi<TeenData[]>({
    url: `v2/admin/export-teen-data?${params}`,
    method: "GET",
  });

  if (!data) {
    throw new Error("Failed to export teen data");
  }

  return data;
};

export const exportIrbSurveyData = async (): Promise<Blob> => {
  const { data } = await fetchApi<Blob>({
    url: `v2/admin/export-irb-survey-data`,
    method: "GET",
    responseType: "blob",
  });

  if (!data) {
    throw new Error("Failed to export IRB survey data");
  }

  return data;
};

export const useGenerateMentorSubheading = () => {
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["generate-mentor-subheading"],
    mutationFn: async (params: {
      teenInterests: string[];
      mentorInterests: string[];
    }) => {
      const { data } = await fetchApi<{ subheading: string }>({
        url: "v2/users/mentor/subheading",
        method: "GET",
        params: {
          teenInterests: params.teenInterests,
          mentorInterests: params.mentorInterests,
        },
      });

      if (!data) {
        throw new Error("Failed to generate subheading");
      }

      return data;
    },
    onError: () => {
      toast({
        title: "Failed to generate subheading",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

// Message Search Types and Hooks
export interface SearchMessagesParams {
  query: string;
  limit?: number;
  exact_match?: boolean;
  advanced_query?: boolean;
  message_ts_from?: number;
  message_ts_to?: number;
  target_user_ids?: string[];
  sort_field?: "score" | "ts";
  reverse?: boolean;
}

export interface SearchResult extends Message {
  score?: number;
}

export interface SearchMessagesResponse {
  results: SearchResult[];
  total_count: number;
  has_next: boolean;
  next?: string;
}

export const useSearchMessages = (mentorshipId: string) => {
  const { toast } = useToast();

  return useMutation({
    mutationKey: ["search-messages", mentorshipId],
    mutationFn: async (
      params: SearchMessagesParams,
    ): Promise<SearchMessagesResponse> => {
      const { data } = await fetchApi<SearchMessagesResponse>({
        url: `v2/admin/mentorships/${mentorshipId}/messages/search`,
        method: "GET",
        params,
      });

      if (!data) {
        throw new Error("Failed to search messages");
      }

      return data;
    },
    onError: () => {
      toast({
        title: "Search failed",
        description: "Please try again",
        variant: "destructive",
      });
    },
  });
};

export interface MessageContextResponse {
  messages: (Message & { isTarget?: boolean })[];
  targetMessageId: number;
}

export const useMessageContext = (
  mentorshipId: string,
  messageId: number,
  contextSize?: number,
) => {
  return useQuery({
    queryKey: ["message-context", mentorshipId, messageId, contextSize],
    queryFn: async (): Promise<MessageContextResponse> => {
      const { data } = await fetchApi<MessageContextResponse>({
        url: `v2/admin/mentorships/${mentorshipId}/messages/${messageId}/context`,
        method: "GET",
        params: { contextSize },
      });

      if (!data) {
        throw new Error("Failed to get message context");
      }

      return data;
    },
    enabled: !!mentorshipId && !!messageId,
  });
};

export interface MentorForecastingData {
  month_end: string;
  total_mentors: number;
  male_mentors: number;
  female_mentors: number;
  total_teens: number;
  total_supervisors: number;
  total_lead_mentors: number;
  deactivated_mentors: number;
}

export const useGetMentorForecastingData = () => {
  return useQuery({
    queryKey: ["mentor-forecasting"],
    queryFn: async () => {
      const { data } = await fetchApi<MentorForecastingData[]>({
        url: "v2/admin/mentor-forecasting",
        method: "GET",
      });

      if (!data) {
        return [];
      }

      return data;
    },
  });
};
